import React, { useState } from "react";
import { Building, formatAddress } from "@akfdev-com/shared/src/models";
import { AddressForm, PartialAddress } from "../ui";
import ConfirmDialog from "../common/ConfirmDialog";

/**
 * Notification preference key set — matches the closed union in
 * `Building.notificationPreferences` shared model (buildings Rule 20).
 * The backend rejects PUT bodies that include any other key with 400.
 */
type NotificationPrefKey =
  | "statementIssued"
  | "expenseCreated"
  | "paymentReminder";

/**
 * Default notification preferences when the record has no field set.
 * Rule 20: an absent field is treated as "all preferences default-on"
 * so users who predate the field see the same behaviour they had before.
 */
const DEFAULT_PREFS: Record<NotificationPrefKey, boolean> = {
  statementIssued: true,
  expenseCreated: true,
  paymentReminder: true,
};

interface BuildingSettingsProps {
  building: Building;
  isEditing: boolean;
  editForm: Partial<Building>;
  handleInputChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  /**
   * Structured-address handler (address-geolocation Rule 21). When the user
   * edits fields inside <AddressForm>, this callback receives the merged
   * PartialAddress. Parent owns the write of both `structuredAddress` and the
   * derived legacy `address` string.
   */
  onStructuredAddressChange?: (next: PartialAddress) => void;
  /**
   * Notification-preferences handler (buildings Rule 20). When a checkbox
   * toggles, this callback receives the full three-key object with the
   * new value. Parent merges it into `editForm.notificationPreferences`
   * and persists on save.
   */
  onNotificationPreferencesChange?: (
    next: Record<NotificationPrefKey, boolean>,
  ) => void;
  /**
   * Archive handler (buildings Rule 20 · Danger Zone). Invoked after the
   * user confirms via the ConfirmDialog. Parent decides how to persist
   * (DELETE /buildings/{id} today; POST /{id}/archive when task 4.5 lands).
   */
  onArchive?: () => void;
}

const BuildingSettings: React.FC<BuildingSettingsProps> = ({
  building,
  isEditing,
  editForm,
  handleInputChange,
  onStructuredAddressChange,
  onNotificationPreferencesChange,
  onArchive,
}) => {
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  // View-mode address: prefer structured (multi-line via formatAddress),
  // fall back to the legacy free-text string, then "Not specified".
  // Never mock (Rule 6 in requirements.md).
  const structuredView = building.structuredAddress
    ? formatAddress(building.structuredAddress)
    : "";
  const displayAddress =
    structuredView.length > 0
      ? structuredView
      : building.address && building.address.trim().length > 0
      ? building.address
      : "Not specified";

  /**
   * Compose the current notification-preferences state, preferring:
   *   1. `editForm.notificationPreferences` (unsaved edits)
   *   2. `building.notificationPreferences` (persisted record)
   *   3. `DEFAULT_PREFS` (all-on fallback per Rule 20)
   */
  const prefs: Record<NotificationPrefKey, boolean> = {
    ...DEFAULT_PREFS,
    ...(building.notificationPreferences ?? {}),
    ...(editForm.notificationPreferences ?? {}),
  };

  const handlePrefToggle = (key: NotificationPrefKey) => {
    const next = { ...prefs, [key]: !prefs[key] };
    onNotificationPreferencesChange?.(next);
  };

  const handleArchiveConfirm = () => {
    setShowArchiveConfirm(false);
    onArchive?.();
  };

  const prefsDisabled = !onNotificationPreferencesChange;
  const archiveDisabled = !onArchive;

  return (
    <div className="p-6">
      <div className="max-w-3xl">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Building Information
        </h3>

        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                Building Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={editForm.name || ""}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>

            {/* Structured Address form (address-geolocation Rule 21). Replaces
                the pre-existing single-line <input name="address">. Legacy
                free-text `Building.address` is retained on the record and
                automatically re-derived via formatAddress on save. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              {editForm.address && !building?.structuredAddress ? (
                <p className="text-xs text-gray-500 mb-2 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                  Auto-parsed from legacy address:{" "}
                  <span className="font-medium">{editForm.address}</span>
                  <br />
                  Review the fields below and correct anything that mis-mapped.
                  The legacy string will be regenerated on save.
                </p>
              ) : null}
              <AddressForm
                value={editForm.structuredAddress ?? {}}
                onChange={onStructuredAddressChange ?? (() => undefined)}
                idPrefix="building-settings"
                disabled={!onStructuredAddressChange}
              />
            </div>

            <div>
              <label
                htmlFor="unitsCount"
                className="block text-sm font-medium text-gray-700"
              >
                Number of Units
              </label>
              <input
                type="number"
                name="unitsCount"
                id="unitsCount"
                value={editForm.unitsCount || ""}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>

            {/* Building schedule (Bucket B #5).
                - builtDate: when the building was constructed. Fully
                  optional; some buildings will never have this recorded.
                - managementStartDate: when the current management
                  company started managing the building. Drives the
                  default `moveInDate` on resident onboarding and any
                  future accrual calculations. Optional; falls back to
                  today when unset. Units may override with their own
                  `Unit.managementStartDate`. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="builtDate"
                  className="block text-sm font-medium text-gray-700"
                >
                  Built Date
                </label>
                <input
                  type="date"
                  name="builtDate"
                  id="builtDate"
                  value={editForm.builtDate || ""}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  When the building was originally constructed. Optional.
                </p>
              </div>
              <div>
                <label
                  htmlFor="managementStartDate"
                  className="block text-sm font-medium text-gray-700"
                >
                  Management Start Date
                </label>
                <input
                  type="date"
                  name="managementStartDate"
                  id="managementStartDate"
                  value={editForm.managementStartDate || ""}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Anchor date for resident onboarding + accrual. Units may
                  override with their own value.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">
                Building Name
              </h4>
              <p className="mt-1 text-sm text-gray-900">
                {building.name || "Not specified"}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-500">Address</h4>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                {displayAddress}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-500">
                Number of Units
              </h4>
              <p className="mt-1 text-sm text-gray-900">
                {building.unitsCount || "Not specified"}
              </p>
            </div>

            {/* Building schedule (Bucket B #5). Read-only view mirrors
                the edit inputs; missing values render as "Not specified"
                so the treasurer can see at a glance whether to fill them. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">
                  Built Date
                </h4>
                <p className="mt-1 text-sm text-gray-900">
                  {building.builtDate
                    ? new Date(building.builtDate).toLocaleDateString()
                    : building.yearBuilt
                    ? String(building.yearBuilt)
                    : "Not specified"}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">
                  Management Start Date
                </h4>
                <p className="mt-1 text-sm text-gray-900">
                  {building.managementStartDate
                    ? new Date(
                        building.managementStartDate,
                      ).toLocaleDateString()
                    : "Not specified"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Notifications — buildings Rule 20.
            Checkboxes are CONTROLLED. Values read from
            `editForm.notificationPreferences` (unsaved) → `building.notificationPreferences`
            (persisted) → DEFAULT_PREFS (all-on). Toggle calls up to the
            parent; the parent persists via PUT /buildings/{id} on Save. */}
        <h3 className="text-lg font-medium text-gray-900 mt-8 mb-4">
          Notifications
        </h3>

        <div className="space-y-4">
          <div className="flex items-center">
            <input
              id="statements-notifications"
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              checked={prefs.statementIssued}
              onChange={() => handlePrefToggle("statementIssued")}
              disabled={prefsDisabled || !isEditing}
            />
            <label
              htmlFor="statements-notifications"
              className={`ml-2 block text-sm ${
                prefsDisabled || !isEditing ? "text-gray-500" : "text-gray-900"
              }`}
            >
              Email residents when new statements are available
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="expense-notifications"
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              checked={prefs.expenseCreated}
              onChange={() => handlePrefToggle("expenseCreated")}
              disabled={prefsDisabled || !isEditing}
            />
            <label
              htmlFor="expense-notifications"
              className={`ml-2 block text-sm ${
                prefsDisabled || !isEditing ? "text-gray-500" : "text-gray-900"
              }`}
            >
              Notify all admins when new expenses are added
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="payment-notifications"
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              checked={prefs.paymentReminder}
              onChange={() => handlePrefToggle("paymentReminder")}
              disabled={prefsDisabled || !isEditing}
            />
            <label
              htmlFor="payment-notifications"
              className={`ml-2 block text-sm ${
                prefsDisabled || !isEditing ? "text-gray-500" : "text-gray-900"
              }`}
            >
              Send payment reminders to residents
            </label>
          </div>

          {!isEditing && (
            <p className="text-xs text-gray-500 italic">
              Enter edit mode above to change notification preferences.
            </p>
          )}
        </div>

        {/* Danger Zone — buildings Rule 20 · Archive Building.
            Reuses the shared <ConfirmDialog> so the confirm step is
            consistent with the page-header Delete flow. */}
        <h3 className="text-lg font-medium text-gray-900 mt-8 mb-4">
          Danger Zone
        </h3>

        <div className="bg-red-50 p-4 rounded-md">
          <h4 className="text-sm font-medium text-red-800">Archive Building</h4>
          <p className="mt-1 text-sm text-red-700">
            Archiving will make the building read-only and hide it from the main
            dashboard. All data will be preserved and can be restored later.
          </p>
          <button
            type="button"
            onClick={() => setShowArchiveConfirm(true)}
            disabled={archiveDisabled}
            className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="archive-building-button"
          >
            Archive Building
          </button>
          {archiveDisabled && (
            <p className="mt-2 text-xs text-red-600 italic">
              Archive is unavailable in this view.
            </p>
          )}
        </div>

        <ConfirmDialog
          isOpen={showArchiveConfirm}
          onClose={() => setShowArchiveConfirm(false)}
          onConfirm={handleArchiveConfirm}
          title="Archive Building"
          message={`Archive "${building.name}"? The building will be hidden from the main dashboard but every record remains readable and can be restored later.`}
          confirmText="Archive"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      </div>
    </div>
  );
};

export default BuildingSettings;
